import { Injectable, inject, resource, computed, signal } from '@angular/core';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { FIRESTORE_TOKEN } from '../firebase/firestore.provider';
import { AuthService } from '../auth/auth.service';
import type { Sensor, SensorHistory, TiltAlert, TiltDisplacement } from '../../shared/interfaces';

export interface TiltDataState {
  sensors: Sensor[];
  displacements: TiltDisplacement[];
  alerts: TiltAlert[];
}

@Injectable({
  providedIn: 'root'
})
export class TiltSensorService {
  private readonly firestore = inject(FIRESTORE_TOKEN);
  private readonly authService = inject(AuthService);

  public readonly siteId = signal<string | null>(null);

  private readonly sensorDataResource = resource({
    params: () => ({
      orgId: this.authService.userOrganizationId(),
      role: this.authService.userRole(),
      siteId: this.siteId()
    }),
    loader: async ({ params: { orgId, role, siteId } }) => {
      if (!siteId) {
        return { sensors: [], displacements: [], alerts: [] };
      }
      if (!orgId && role !== 'super_admin') {
        return { sensors: [], displacements: [], alerts: [] };
      }

      try {
        const sensorsRef = collection(this.firestore, 'sensors');
        let sensorsQuery;
        if (role === 'super_admin') {
          sensorsQuery = query(
            sensorsRef,
            where('siteId', '==', siteId),
            where('categoryName', '==', 'TILT'),
            where('deletedAt', '==', null)
          );
        } else {
          sensorsQuery = query(
            sensorsRef,
            where('organizationId', '==', orgId),
            where('siteId', '==', siteId),
            where('categoryName', '==', 'TILT'),
            where('deletedAt', '==', null)
          );
        }
        
        const sensorsSnapshot = await getDocs(sensorsQuery);
        const sensors = sensorsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Sensor));

        if (sensors.length === 0) {
          return { sensors, displacements: [], alerts: [] };
        }

        const now = new Date();
        const fromDate = new Date(now);
        // Only get data from the last 2 days to have enough data for 1h difference if necessary. 
        // For displacement, we just need the latest value and the value 1h ago for alerts.
        fromDate.setDate(now.getDate() - 2);

        // MQTT は date フィールドを "YYYY-MM-DD HH:mm:ss" 形式の文字列で保存
        const fromDateStr = this.formatDateString(fromDate);

        const displacements: TiltDisplacement[] = [];
        const alerts: TiltAlert[] = [];
        
        await Promise.all(sensors.map(async (sensor) => {
          const historyRef = collection(this.firestore, `sensors/${sensor.id}/history`);
          const historyQuery = query(
            historyRef,
            where('date', '>=', fromDateStr),
            orderBy('date', 'desc'),
            limit(100) // Increase limit to fetch enough history for 1h ago
          );
          
          const historySnapshot = await getDocs(historyQuery);
          const histories = historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as SensorHistory));
          
          if (histories.length > 0) {
            const latest = histories[0];
            const sensorData = sensor.data || {};
            const depth = Number(sensorData['depth']) || 0;
            const calX = Number(sensorData['calibrationX']) || 0;
            const calY = Number(sensorData['calibrationY']) || 0;
            
            // MQTT はフラット構造で tiltX, tiltY をルートレベルに保存
            const tiltX = Number(latest.tiltX ?? latest.data?.['tiltX']) || 0;
            const tiltY = Number(latest.tiltY ?? latest.data?.['tiltY']) || 0;
            
            const dispX = tiltX - calX;
            const dispY = tiltY - calY;

            // 日時: date 文字列 or measuredAt Timestamp
            const latestTimestamp = latest.date
              ? new Date(latest.date.replace(' ', 'T') + '+09:00').toISOString()
              : (latest.measuredAt ? latest.measuredAt.toDate().toISOString() : new Date().toISOString());
            
            displacements.push({
              sensorId: sensor.id,
              sensorName: String(sensorData['name'] || sensor.id),
              depth,
              displacementX: dispX,
              displacementY: dispY,
              timestamp: latestTimestamp
            });

            // Calculate alert (1 hour diff)
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            // find closest history to 1 hour ago
            const history1h = histories.find(h => {
              let t: number;
              if (h.date) {
                t = new Date(h.date.replace(' ', 'T') + '+09:00').getTime();
              } else if (h.measuredAt) {
                t = h.measuredAt.toDate().getTime();
              } else {
                return false;
              }
              return t <= oneHourAgo.getTime();
            });

            if (history1h) {
              // フラットフィールド or 旧 data ネスト
              const oldTiltX = Number(history1h.tiltX ?? history1h.data?.['tiltX']) || 0;
              const oldTiltY = Number(history1h.tiltY ?? history1h.data?.['tiltY']) || 0;
              
              const diffX = Math.abs(tiltX - oldTiltX);
              const diffY = Math.abs(tiltY - oldTiltY);
              
              const threshold = Number(sensorData['alertThreshold']) || 1.0; // Default threshold

              if (diffX > threshold) {
                alerts.push({
                  sensorId: sensor.id,
                  sensorName: String(sensorData['name'] || sensor.id),
                  depth,
                  axis: 'X',
                  displacementDiff: diffX,
                  threshold,
                  timestamp: latestTimestamp
                });
              }
              if (diffY > threshold) {
                alerts.push({
                  sensorId: sensor.id,
                  sensorName: String(sensorData['name'] || sensor.id),
                  depth,
                  axis: 'Y',
                  displacementDiff: diffY,
                  threshold,
                  timestamp: latestTimestamp
                });
              }
            }
          }
        }));

        return { sensors, displacements, alerts };
      } catch (err) {
        console.error('傾斜センサーデータの取得に失敗しました:', err);
        throw new Error('傾斜センサーデータの取得に失敗しました', { cause: err });
      }
    }
  });

  public readonly sensors = computed(() => this.sensorDataResource.value()?.sensors ?? []);
  public readonly latestDisplacements = computed(() => this.sensorDataResource.value()?.displacements ?? []);
  public readonly activeAlerts = computed(() => this.sensorDataResource.value()?.alerts ?? []);
  
  public readonly isLoading = computed(() => 
    this.sensorDataResource.status() === 'loading' || 
    this.sensorDataResource.status() === 'reloading'
  );

  public readonly error = computed(() => {
    const err = this.sensorDataResource.error();
    return err instanceof Error ? err.message : null;
  });

  /**
   * Date オブジェクトを MQTT 互換の "YYYY-MM-DD HH:mm:ss" 形式文字列に変換する。
   * タイムゾーンは Asia/Tokyo (JST) を使用する。
   */
  private formatDateString(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const jst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    return `${jst.getUTCFullYear()}-${pad(jst.getUTCMonth() + 1)}-${pad(jst.getUTCDate())} ${pad(jst.getUTCHours())}:${pad(jst.getUTCMinutes())}:${pad(jst.getUTCSeconds())}`;
  }
}
